import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';

const AdminAffiliate = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  // Multi-user selection to allow choosing accounts across users
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [accountsByUser, setAccountsByUser] = useState({}); // { [userId]: Account[] }
  const [affiliateMappings, setAffiliateMappings] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState(new Set());
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null); // user picker dropdown

  const token = useMemo(() => localStorage.getItem('token'), []);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${config.apiUrl}/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to load users');
        const data = await res.json();
        setUsers(data.users || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [token, navigate]);

  // Close the user picker dropdown when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (!userDropdownOpen) return;
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userDropdownOpen]);

  const affiliateCandidates = users; // Admin can toggle visibility for any user
  const affiliates = users.filter(u => u.affiliate_tab_visible || false);

  // Memo: set of account IDs already mapped to the selected affiliate
  const mappedAccountIdSet = useMemo(() => {
    const s = new Set();
    if (affiliateMappings && affiliateMappings.length) {
      for (const m of affiliateMappings) {
        const id = (m.mt5_account && m.mt5_account.id) || m.mt5_account_id;
        if (id != null) s.add(id);
      }
    }
    return s;
  }, [affiliateMappings]);

  // Memo: account IDs that are eligible to be selected (i.e. not already linked)
  const selectableAccountIds = useMemo(() => {
    const ids = [];
    for (const uid of Object.keys(accountsByUser)) {
      const arr = accountsByUser[uid] || [];
      for (const acc of arr) {
        if (!mappedAccountIdSet.has(acc.id)) ids.push(acc.id);
      }
    }
    return ids;
  }, [accountsByUser, mappedAccountIdSet]);

  const toggleAffiliateVisibility = async (userId, nextVisible) => {
    try {
      setIsBusy(true);
      const res = await fetch(`${config.apiUrl}/admin/users/${userId}/affiliate-visible`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ affiliate_tab_visible: nextVisible })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Failed to update: ${res.status} ${txt}`);
      }
      // Optimistic update
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, affiliate_tab_visible: nextVisible } : u));
    } catch (e) {
      alert(e.message);
    } finally {
      setIsBusy(false);
    }
  };

  const toggleHideAccountDetails = async (userId, nextHide) => {
    try {
      setIsBusy(true);
      const res = await fetch(`${config.apiUrl}/admin/users/${userId}/affiliate-hide-details`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ affiliate_hide_account_details: nextHide })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Failed to update: ${res.status} ${txt}`);
      }
      // Optimistic update
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, affiliate_hide_account_details: nextHide } : u));
    } catch (e) {
      alert(e.message);
    } finally {
      setIsBusy(false);
    }
  };

  // Fetch accounts for a user and store
  const fetchAccountsForUser = async (userId, ownerEmail) => {
    const res = await fetch(`${config.apiUrl}/admin/users/${userId}/accounts`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to load accounts');
    const data = await res.json();
    // Tag accounts with owner email for display grouping
    const withOwner = (Array.isArray(data) ? data : []).map(a => ({ ...a, owner_email: ownerEmail }));
    setAccountsByUser(prev => ({ ...prev, [userId]: withOwner }));
  };

  // Toggle a user in the multi-select list
  const toggleUserSelection = async (user) => {
    const uid = user.id;
    const willSelect = !selectedUserIds.has(uid);
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (willSelect) next.add(uid); else next.delete(uid);
      return next;
    });
    if (willSelect) {
      if (!accountsByUser[uid]) {
        try { await fetchAccountsForUser(uid, user.email); } catch (e) { alert(e.message); }
      }
    } else {
      // If user deselected, drop their accounts from current selection
      setSelectedAccountIds(prev => {
        const next = new Set(prev);
        for (const acc of (accountsByUser[uid] || [])) next.delete(acc.id);
        return next;
      });
    }
  };

  const loadMappings = async (affiliateId) => {
    try {
      setSelectedAffiliate(affiliateId);
      const res = await fetch(`${config.apiUrl}/admin/affiliates/${affiliateId}/mappings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load mappings');
      const data = await res.json();
      setAffiliateMappings(Array.isArray(data) ? data : []);
    } catch (e) {
      alert(e.message);
    }
  };

  // Helper: is this account already mapped to the currently selected affiliate?
  const isAccountMappedToSelectedAffiliate = (accId) => mappedAccountIdSet.has(accId);

  // Toggle selection for an account (skip mapped ones)
  const toggleAccountSelect = (accId) => {
    if (isAccountMappedToSelectedAffiliate(accId)) return; // can't select already linked
    setSelectedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accId)) next.delete(accId); else next.add(accId);
      return next;
    });
  };

  const selectAllAccounts = () => {
    setSelectedAccountIds(new Set(selectableAccountIds));
  };

  const clearSelection = () => setSelectedAccountIds(new Set());

  // note: single-account linking is supported by selecting just one account
  // and using addMappingsBulk(), so the per-row addMapping helper was removed.

  // New: bulk add mappings for selectedAccountIds
  const addMappingsBulk = async () => {
    if (!selectedAffiliate || selectedAccountIds.size === 0) return;
    try {
      setIsBusy(true);
      // Perform requests sequentially to be gentle on the API and avoid race refreshes
      for (const accId of selectedAccountIds) {
        // Skip if somehow mapped meanwhile
        if (isAccountMappedToSelectedAffiliate(accId)) continue;
        const res = await fetch(`${config.apiUrl}/admin/affiliates/${selectedAffiliate}/mappings`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ mt5_account_id: accId })
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Failed to add mapping for account ${accId}: ${res.status} ${txt}`);
        }
      }
      // Refresh mappings once at the end
      await loadMappings(selectedAffiliate);
      clearSelection();
      alert('Selected accounts linked');
    } catch (e) {
      alert(e.message);
    } finally {
      setIsBusy(false);
    }
  };

  const deleteMapping = async (affiliateId, mappingId) => {
    try {
      setIsBusy(true);
      const res = await fetch(`${config.apiUrl}/admin/affiliates/${affiliateId}/mappings/${mappingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete mapping');
      await loadMappings(affiliateId);
    } catch (e) {
      alert(e.message);
    } finally {
      setIsBusy(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold">Affiliate Management</h1>

      {/* Section 1: Toggle affiliate tab visibility for users */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Affiliate Tab Visibility</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Affiliate Visible</th>
                <th className="text-left p-2" title="When checked, account number and email are hidden from the affiliate view; only balance, equity, deposits, withdrawals, open positions and history are shown">
                  Hide Acc. Details
                </th>
              </tr>
            </thead>
            <tbody>
              {affiliateCandidates.map(u => (
                <tr key={u.id} className="border-b">
                  <td className="p-2">{u.id}</td>
                  <td className="p-2">{u.email}</td>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={!!u.affiliate_tab_visible}
                      onChange={(e) => toggleAffiliateVisibility(u.id, e.target.checked)}
                      disabled={isBusy}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={!!u.affiliate_hide_account_details}
                      onChange={(e) => toggleHideAccountDetails(u.id, e.target.checked)}
                      disabled={isBusy}
                      title="Hide account number and email from affiliate view"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2: Manage affiliate mappings */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Affiliate ↔ Account Mappings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h3 className="font-medium mb-2">Select Affiliate</h3>
            <ul className="border rounded divide-y">
              {affiliates.length === 0 && (
                <li className="p-2 text-sm text-gray-500">No affiliates enabled yet</li>
              )}
              {affiliates.map(a => (
                <li key={a.id} className={`p-2 cursor-pointer ${selectedAffiliate === a.id ? 'bg-indigo-50' : ''}`}
                    onClick={() => loadMappings(a.id)}>
                  {a.email}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-2">Pick user, then select accounts</h3>
            <div className="relative mb-2" ref={userDropdownRef}>
              <button
                type="button"
                className={`w-full justify-between inline-flex items-center border rounded px-3 py-2 text-sm bg-white`}
                onClick={() => setUserDropdownOpen(v => !v)}
                title="Choose user(s)"
              >
                <span>
                  {selectedUserIds.size > 0
                    ? `${selectedUserIds.size} user(s) selected`
                    : 'Choose user(s)'}
                </span>
                <svg className="w-4 h-4 ml-2 opacity-70" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd"/></svg>
              </button>
              {userDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto bg-white border rounded shadow">
                  {users.map(u => {
                    const selected = selectedUserIds.has(u.id);
                    return (
                      <label
                        key={u.id}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 ${selected ? 'bg-indigo-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-indigo-600"
                          checked={selected}
                          onChange={() => toggleUserSelection(u)}
                        />
                        <span className="truncate">{u.email}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Accounts multi-select list (always visible) */}
            <div className="border rounded">
              <div className="flex items-center justify-between p-2 border-b bg-white">
                <div className="text-xs text-gray-600">
                  {selectedUserIds.size > 0
                    ? `Accounts for ${selectedUserIds.size} user(s)`
                    : 'Choose user(s) to see accounts'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-2 py-1 text-xs border rounded"
                    onClick={selectAllAccounts}
                    disabled={selectedUserIds.size === 0 || isBusy || selectableAccountIds.length === 0}
                  >
                    {`Select all${selectableAccountIds.length ? ` (${selectableAccountIds.length})` : ''}`}
                  </button>
                  <button
                    className="px-2 py-1 text-xs border rounded"
                    onClick={clearSelection}
                    disabled={selectedAccountIds.size === 0 || isBusy}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-64 overflow-auto">
                {(selectedUserIds.size === 0 || Array.from(selectedUserIds).every(uid => !(accountsByUser[uid] && accountsByUser[uid].length))) && (
                  <div className="p-2 text-sm text-gray-500">{selectedUserIds.size > 0 ? 'No accounts' : 'No user selected'}</div>
                )}
                {Array.from(selectedUserIds).flatMap(uid => accountsByUser[uid] || []).map(acc => {
                  const mapped = isAccountMappedToSelectedAffiliate(acc.id);
                  const checked = selectedAccountIds.has(acc.id);
                  return (
                    <label key={acc.id} className="flex items-center justify-between gap-2 p-2 hover:bg-gray-50 cursor-pointer">
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-indigo-600 cursor-pointer"
                          checked={checked}
                          disabled={mapped}
                          onChange={() => toggleAccountSelect(acc.id)}
                        />
                        <span>
                          <span className="font-mono text-sm">{acc.account_number}</span>
                          <span className="block text-xs text-gray-500">{acc.broker_name} / {acc.broker_server} • {acc.owner_email}</span>
                        </span>
                      </span>
                      {mapped ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700">Linked</span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Actions under the dropdown */}
            <div className="flex items-center gap-2 mt-2">
              <button
                className="px-3 py-1 text-sm bg-green-600 text-white rounded disabled:opacity-50"
                disabled={!selectedAffiliate || selectedAccountIds.size === 0 || isBusy}
                onClick={addMappingsBulk}
              >
                Link selected ({selectedAccountIds.size})
              </button>
              <span className="text-xs text-gray-500">Tip: Use the checkmarks to multi‑select accounts, or "Select all"/"Clear".</span>
            </div>
          </div>
          <div>
            <h3 className="font-medium mb-2">Mappings for selected affiliate</h3>
            <div className="border rounded divide-y max-h-64 overflow-auto">
              {(!affiliateMappings || affiliateMappings.length === 0) && (
                <div className="p-2 text-sm text-gray-500">No mappings</div>
              )}
              {affiliateMappings.map(m => (
                <div key={m.mapping_id} className="p-2 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-sm">{m.mt5_account?.account_number}</div>
                    <div className="text-xs text-gray-500">Owner: {m.mt5_account?.owner_email}</div>
                  </div>
                  <button
                    className="px-2 py-1 text-sm bg-red-600 text-white rounded disabled:opacity-50"
                    disabled={!selectedAffiliate || isBusy}
                    onClick={() => deleteMapping(selectedAffiliate, m.mapping_id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAffiliate;
